import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import Const from '../Constants';
import './tour.css';

/*
  Anchored product tour for new users. Each step highlights an always-mounted
  sidebar element and, as the step starts, flips `appMode` to that view so the
  user sees the real screen behind the spotlight. See the plan / SideBar.js for
  the `data-tour` anchors.
*/
export function startProductTour({ setAppMode, onDone }) {
  // Switch the main view to `mode` when a step begins.
  const showView = (mode) => () => {
    if (mode) {
      setAppMode(mode);
    }
  };

  const driverObj = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: '#000000',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Got it',
    popoverClass: 'flowhero-tour',
    onDestroyed: () => {
      // Land the user on Notes (the Getting Started overview) once the tour finishes (or is closed).
      setAppMode(Const.sNotes);
      onDone?.();
    },
    steps: [
      {
        // Centered welcome — no anchored element. The view is set to One on Ones
        // explicitly before drive() below (driver.js won't fire onHighlightStarted
        // for an elementless step).
        popover: {
          title: `Welcome to ${Const.sAppName}!`,
          description:
            'Your workspace for managing people and work — one-on-ones, meetings, interviews, tasks, notes, and AI agents. Let’s take a quick tour.'
        }
      },
      {
        element: '[data-tour="one-on-ones"]',
        onHighlightStarted: showView(Const.s1on1Meetings),
        popover: {
          side: 'right',
          align: 'start',
          title: 'One on Ones',
          description:
            'Run great 1:1s — keep a running agenda and action items for each person you meet with.'
        }
      },
      {
        element: '[data-tour="meetings"]',
        onHighlightStarted: showView(Const.sMeetings),
        popover: {
          side: 'right',
          align: 'start',
          title: 'Meetings',
          description:
            'Shared notes, agendas, and action items for all your meetings — all in one place.'
        }
      },
      {
        element: '[data-tour="interviews"]',
        onHighlightStarted: showView(Const.sInterviews),
        popover: {
          side: 'right',
          align: 'start',
          title: 'Interviews',
          description:
            'Capture interview notes against reusable templates, with AI-assisted summaries.'
        }
      },
      {
        element: '[data-tour="tasks"]',
        onHighlightStarted: showView(Const.sTasks),
        popover: {
          side: 'right',
          align: 'start',
          title: 'Tasks',
          description:
            'A simple board to track what you’re working on — drag cards across To Do, In Progress, and Done.'
        }
      },
      {
        element: '[data-tour="notes"]',
        onHighlightStarted: showView(Const.sNotes),
        popover: {
          side: 'right',
          align: 'start',
          title: 'Notes',
          description:
            'Jot down anything and organize it into groups — your free-form scratchpad.'
        }
      },
      {
        element: '[data-tour="agents"]',
        onHighlightStarted: showView(Const.sAgents),
        popover: {
          side: 'right',
          align: 'start',
          title: 'AI Agents',
          description:
            'Define your own AI agents with a role and briefing, then assign tasks to them to do the busywork for you.'
        }
      },
      {
        element: '[data-tour="profile"]',
        popover: {
          side: 'bottom',
          align: 'start',
          title: 'Your account',
          description:
            'Manage your profile and settings here. You can replay this tour anytime from Profile.'
        }
      }
    ]
  });

  // Start on One on Ones explicitly: the welcome step has no anchor element, so
  // driver.js won't fire its per-step onHighlightStarted to switch the view.
  setAppMode(Const.s1on1Meetings);

  driverObj.drive();
  return driverObj;
}
